import React, { useState } from 'react';
import { useEVM } from '../contexts/EVMProvider';
import { Wallet, X, ExternalLink } from 'lucide-react';

// Import wallet images
import MetaMaskWallet from '../walls/metamask.webp';
import TrustWallet from '../walls/trust.webp';
import CoinbaseWallet from '../walls/coinbase.webp';
import BinanceWallet from '../walls/Binance.webp';
import OKXWallet from '../walls/OKX.webp';
import BitgetWallet from '../walls/bitget.webp';
import BraveWallet from '../walls/bravel.webp';
import BybitWallet from '../walls/bybit.webp';
import CryptoDotComWallet from '../walls/cryptodotcom.webp';
import BlockchainWallet from '../walls/blockchain.webp';
import SafePalWallet from '../walls/safepal.webp';
import UniswapWallet from '../walls/uniswap.webp';
import ZealWallet from '../walls/zeal.webp';
import ZerionWallet from '../walls/zerion.webp';

const EVMWalletConnector = () => {
  const { connectEVMWallet } = useEVM();
  const [loading, setLoading] = useState(false);
  const [showWalletSelector, setShowWalletSelector] = useState(false);

  // Supported EVM wallets
  const wallets = [
    { name: 'MetaMask', image: MetaMaskWallet, id: 'metamask' },
    { name: 'Trust Wallet', image: TrustWallet, id: 'trust' },
    { name: 'Coinbase Wallet', image: CoinbaseWallet, id: 'coinbase' },
    { name: 'Binance Wallet', image: BinanceWallet, id: 'binance' },
    { name: 'OKX Wallet', image: OKXWallet, id: 'okx' },
    { name: 'Bitget Wallet', image: BitgetWallet, id: 'bitget' },
    { name: 'Brave Wallet', image: BraveWallet, id: 'brave' },
    { name: 'Bybit Wallet', image: BybitWallet, id: 'bybit' },
    { name: 'Crypto.com Wallet', image: CryptoDotComWallet, id: 'cryptodotcom' },
    { name: 'Blockchain.com', image: BlockchainWallet, id: 'blockchain' },
    { name: 'SafePal Wallet', image: SafePalWallet, id: 'safepal' },
    { name: 'Uniswap Wallet', image: UniswapWallet, id: 'uniswap' },
    { name: 'Zeal Wallet', image: ZealWallet, id: 'zeal' },
    { name: 'Zerion Wallet', image: ZerionWallet, id: 'zerion' },
  ];

  // Function to detect if wallet is available
  const isWalletAvailable = (walletId) => {
    // Check for any Ethereum wallet
    if (typeof window.ethereum !== 'undefined') {
      return true;
    }
    // Check for Binance Chain
    if (typeof window.BinanceChain !== 'undefined') {
      return true;
    }
    return false;
  };

  const handleConnect = async (walletId) => {
    try {
      setLoading(true);
      setShowWalletSelector(false);
      const result = await connectEVMWallet(walletId);
      
      if (!result.success) {
        console.error('Connection failed:', result.error);
        // The error handling is now done in the EVMProvider
      }
    } catch (error) {
      console.error('Connection failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowWalletSelector(true)}
        disabled={loading}
        className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[44px] sm:min-h-[48px]"
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="loading-spinner"></div>
            <span>Connecting...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2">
            <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Connect EVM Wallet</span>
            <span className="sm:hidden">Connect Wallet</span>
          </div>
        )}
      </button>

      {/* Wallet Selector Modal */}
      {showWalletSelector && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            onClick={() => setShowWalletSelector(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center p-2 sm:p-4 pt-16 sm:pt-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm sm:max-w-md h-[calc(100vh-8rem)] sm:h-auto sm:max-h-[600px] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Connect EVM (Ethereum) Wallet
                </h2>
                <button
                  onClick={() => setShowWalletSelector(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[32px] min-w-[32px] flex items-center justify-center"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Wallet Grid - Scrollable */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {wallets.map((wallet) => {
                    const isAvailable = isWalletAvailable(wallet.id);
                    return (
                      <button
                        key={wallet.id}
                        onClick={() => handleConnect(wallet.id)}
                        disabled={loading}
                        className={`flex flex-col items-center p-3 sm:p-4 rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[80px] sm:min-h-[100px] touch-manipulation ${
                          isAvailable 
                            ? 'border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-orange-500 dark:hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                        }`}
                      >
                        <div className="w-8 h-8 sm:w-12 sm:h-12 mb-2 sm:mb-3 flex items-center justify-center relative">
                          <img 
                            src={wallet.image} 
                            alt={wallet.name} 
                            className="w-full h-full object-contain rounded-lg"
                          />
                          {!isAvailable && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-orange-500 rounded-full flex items-center justify-center">
                              <ExternalLink className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-center text-gray-900 dark:text-white leading-tight">
                          {wallet.name}
                        </span>
                        {!isAvailable && (
                          <span className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            Install
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center">
                  {isWalletAvailable('metamask') ? 'Choose your preferred Ethereum wallet to connect' : 'Some wallets may need to be installed first'}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default EVMWalletConnector;
